/* eslint-disable spire/fenced-imports */
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Icon from "@insite/mobius/Icon";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import TextField from "@insite/mobius/TextField";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import { EditorTemplateProps } from "@insite/shell-public/EditorTemplateProps";
import ClickOutside from "@insite/shell/Components/ClickOutside";
import ArrowDown from "@insite/shell/Components/Icons/ArrowDown";
import ArrowRight from "@insite/shell/Components/Icons/ArrowRight";
import Link from "@insite/shell/Components/Icons/Link";
import shellTheme, { ShellThemeProps } from "@insite/shell/ShellTheme";
import ShellState from "@insite/shell/Store/ShellState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";
/* eslint-enable spire/fenced-imports */

interface State {
    linkSelectorIsOpen: boolean;
    expandedKeys: string[];
    searchValue: string;

    url: string;
    isValidUrl: boolean;
    triedToSaveUrl: boolean;
}

type OwnProps = EditorTemplateProps<LinkFieldValue, LinkFieldDefinition>;

const mapStateToProps = (state: ShellState, ownProps: OwnProps) => {
    const fieldValue = ownProps.fieldValue;
    const currentPage = getCurrentPage(state);
    const displayValue = getPageLinkByNodeId(state, fieldValue.value)?.title ?? "";

    const flatPages: PageLinkModel[] = [];
    const loadBlogLinks = (page: PageLinkModel) => {
        if (!page.children || page.children.length === 0) {
            flatPages.push(page);
            return;
        }

        for (const child of page.children) {
            loadBlogLinks(child);
        }
        flatPages.push({
            ...page,
            children: [],
        });
    };

    const home = state.links.pageLinks[0];
    loadBlogLinks(home);
    const blogLinks = flatPages
        .filter(page => page.type === "BlogDetailPage")
        .filter(page => {
            if (currentPage.type === "BlogDetailPage") {
                return page.parentId === currentPage.parentId && page.id !== currentPage.nodeId;
            }
            return true;
        });
    return {
        blogLinks,
        displayValue,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StandardIconPropsSource: React.FC = () => <Link color1={shellTheme.colors.text.main} />;
const ClearIconPropsSource: React.FC = () => <Icon src="X" />;

class CustomBlogLinkField extends ClickOutside<Props, State> {
    constructor(props: Props) {
        super(props);

        const expandedKeys: string[] = [];
        if (props.blogLinks.length > 0) {
            expandedKeys.push(props.blogLinks[0].id);
        }
        expandedKeys.push(emptyGuid);

        this.state = {
            linkSelectorIsOpen: false,
            expandedKeys,
            searchValue: props.displayValue,
            url: props.fieldValue.value,
            isValidUrl: true,
            triedToSaveUrl: false,
        };
    }

    onClickOutside(): void {
        this.setState({
            linkSelectorIsOpen: false,
            searchValue: this.props.displayValue,
        });
    }

    onLinkFieldChange = (event: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            searchValue: event.currentTarget.value,
        });
    };

    clickLinkField = (event: React.FormEvent<HTMLInputElement>) => {
        this.setState({
            linkSelectorIsOpen: true,
        });
    };

    clickPage = (event: React.FormEvent<HTMLElement>) => {
        const nodeId = event.currentTarget.closest("[data-id]")!.getAttribute("data-id")!;
        const blogInformation = this.props.blogLinks.find(link => link.id === nodeId);
        this.props.updateField(this.props.fieldDefinition.name, {
            ...blogInformation,
            value: nodeId,
        } as any);

        this.setState({
            linkSelectorIsOpen: false,
            searchValue: blogInformation!.title,
        });
    };

    clickExpand = (event: React.FormEvent<HTMLElement>) => {
        const nodeId = event.currentTarget.closest("[data-id]")!.getAttribute("data-id")!;
        const index = this.state.expandedKeys.indexOf(nodeId);
        const expandedKeys = this.state.expandedKeys;
        if (index >= 0) {
            expandedKeys.splice(index, 1);
        } else {
            expandedKeys.push(nodeId);
        }

        this.setState({
            expandedKeys,
        });
    };

    renderTreeChunk(links: readonly PageLinkModel[]) {
        return (
            <ul>
                {links
                    .filter(link => link.title.toLowerCase().indexOf(this.state.searchValue.toLowerCase()) > -1)
                    .map(page => {
                        const isExpanded = this.state.expandedKeys.indexOf(page.id) >= 0;
                        const hasChildren = !!page.children;
                        return (
                            <TreeItemStyle key={page.id}>
                                <TitleStyle data-id={page.id}>
                                    {hasChildren && (
                                        <ArrowContainer isExpanded={isExpanded} onClick={this.clickExpand}>
                                            {isExpanded ? <ArrowDown height={6} /> : <ArrowRight width={6} />}
                                        </ArrowContainer>
                                    )}
                                    <span onClick={this.clickPage}>{page.title}</span>
                                </TitleStyle>
                                {isExpanded && hasChildren && this.renderTreeChunk(page.children!)}
                            </TreeItemStyle>
                        );
                    })}
            </ul>
        );
    }

    positionLinkSelector = () => {
        if (!this.wrapperRef) {
            return;
        }

        const element = this.wrapperRef as HTMLElement;

        const rect = (element.previousSibling! as Element).getBoundingClientRect();
        element.style.left = `${rect.left}px`;
        element.style.top = `${rect.top + rect.height}px`;
    };

    clearFieldValue = () => {
        this.props.updateField(this.props.fieldDefinition.name, {
            ...this.props.fieldValue,
            value: "",
        });
    };

    render() {
        setTimeout(() => {
            this.positionLinkSelector();
        });
        const {
            fieldValue: { value },
            fieldDefinition,
        } = this.props;
        const currentTab = "Blog";

        const tabs = [
            <Tab data-test-selector="tab_Pages" headline="Blogs" key="Blog" tabKey="Blog" css={tabCss}>
                {this.renderTreeChunk(this.props.blogLinks)}
            </Tab>,
        ];

        return (
            <StandardControl fieldDefinition={fieldDefinition}>
                <TextField
                    id={fieldDefinition.name}
                    type="text"
                    value={this.state.searchValue}
                    onClick={this.clickLinkField}
                    onChange={this.onLinkFieldChange}
                    iconProps={{ src: value ? ClearIconPropsSource : StandardIconPropsSource }}
                    iconClickableProps={value ? { onClick: this.clearFieldValue } : undefined}
                    clickableText={value ? translate("clear link field") : undefined}
                />
                {this.state.linkSelectorIsOpen && (
                    <LinkSelectorStyle ref={this.setWrapperRef}>
                        <TabGroup cssOverrides={{ tabContent, tabGroup, wrapper }} current={currentTab}>
                            {tabs}
                        </TabGroup>
                    </LinkSelectorStyle>
                )}
            </StandardControl>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomBlogLinkField as any);

const tabCss: FlattenSimpleInterpolation = css`
    padding: 6px;
    &:hover {
        cursor: ${(props: TabProps) => (props.selected ? "default" : "pointer")};
    }
    font-weight: ${(props: TabProps) => (props.selected ? "bold" : "inherit")};
` as FlattenSimpleInterpolation;

const tabContent = css`
    border-bottom: none;
    padding: 16px;
    max-height: 463px;
    overflow: auto;
`;

const tabGroup = css`
    padding: 0;
`;

const wrapper = css`
    padding: 0;
`;

const LinkSelectorStyle = styled.div`
    margin-top: 2px;
    position: absolute;
    background-color: ${(props: ShellThemeProps) => props.theme.colors.common.accent};
    width: 288px;
    z-index: 10;
    border: 1px solid ${(props: ShellThemeProps) => props.theme.colors.common.border};
    border-radius: 3px;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
    height: 500px;
`;

const TitleStyle = styled.p`
    span {
        color: ${(props: ShellThemeProps) => props.theme.colors.primary.main};
        cursor: pointer;

        &:hover {
            font-weight: 400;
            text-shadow: 0 0 1px ${(props: ShellThemeProps) => props.theme.colors.primary.main};
        }
    }
`;

const ArrowContainer = styled.div<{ isExpanded: boolean }>`
    padding: ${props => (props.isExpanded ? "0 6px" : "0 3px 0 4px")};
    margin-left: ${props => (props.isExpanded ? "-5px" : "-1px")};
    display: inline-block;
    cursor: pointer;

    &:hover svg path {
        stroke-width: 4 !important;
    }
`;

const TreeItemStyle = styled.li`
    ul {
        padding-left: 10px;

        p,
        ul {
            margin-left: 12px;
        }

        li {
            position: relative;
        }

        li::before {
            content: "";
            width: 2px;
            height: 100%;
            background-color: rgb(216, 216, 216);
            position: absolute;
            top: 2px;
            left: -6px;
        }

        li:last-child::before {
            height: 10px;
        }

        li::after {
            content: "";
            width: 14px;
            height: 2px;
            background-color: rgb(216, 216, 216);
            position: absolute;
            top: 10px;
            left: -6px;
        }
    }

    & + & {
        margin-top: 5px;
        padding-top: 5px;
        border-top: 1px dashed ${(props: ShellThemeProps) => props.theme.colors.common.border};
    }
`;
